import {Component} from 'react';
import {connect} from 'react-redux';
import injectSheet from 'react-jss';
import Dropzone from 'react-dropzone';

import Dialog from './Dialog';
import StipsService from '-/services/StipsService';
import {getStipIcon} from '-/icons/icons';
import {findImageData, applicantNameSelector} from '-/selectors';
import Loader from '-/components/general/Loader';
import {getOfferId, getVinVehicleInfo, isMobile} from '-/util/util';
import {uploadImage, downloadImage} from '-/api/api';
import {renderError} from '-/util/error';
import MixpanelService from '-/services/MixpanelService';
import Button from '-/components/general/Button';
import StepButtons from '-/components/general/StepButtons';
import {important} from '-/styles/utils';

const styles = () => ({
    root: {
        height: '80%',
        width: '60%',
        alignItems: 'center',
        textAlign: 'center'
    },
    imgContainer: {
        textAlign: 'center',
        position: 'absolute',
        top: 50,
        bottom: 75,
        left: 0,
        right: 0,
        margin: 5,
        '& .loading': {
            maxWidth: 'unset'
        },
        '& img': {
            maxWidth: '100%',
            maxHeight: '100%',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%) translateX(-50%)',
            left: '50%'
        }
    },
    input: {
        width: .1,
        height: .1,
        opacity: 0,
        overflow: 'hidden',
        position: 'absolute',
        zIndex: -1
    },
    dropZone: {
        position: 'absolute !important',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
    },
    please: {
        fontSize: important(15),
        lineHeight: important('unset'),
        padding: important('0 20px')
    },
    footer: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 15
    }
});

class StipDialog extends Component {
    constructor(props) {
        super(props);
        this.state = {
            uploading: false,
            uploadingPercentage: 0,
            error: null,
            downloading: false,
            didUpload: false,
            isPDF: false
        };
    }

    setError(error) {
        const {errorHandler} = this.props;
        MixpanelService.stipImageUpload(false, error);
        this.setState({
            error
        });

        errorHandler(error);
    }

    handleClose = () => {
        StipsService.closeDialog();
    };

    handleRetakeSingle = e => {
        this.handleRetake(e.target.files);
    };

    handleRetake = files => {
        if (!files || !files.length) {
            this.setError('file does not exist.');
            return;
        }

        const [file] = files;
        if (/^image\//i.test(file.type)) {
            this.readFile(file);
        } else if (/^application\/pdf/i.test(file.type)) {
            this.readPDFFile(file);
        } else {
            this.setError(file.type + ' is not a valid image.');
        }
    };

    readFile(file) {
        const reader = new FileReader();
        reader.onloadend = () => {
            this.processFile(reader.result, file.type);
        };
        reader.onerror = err => {
            this.setError('There was an error reading the file: ' + err.toString());
        };
        reader.readAsDataURL(file);
    }

    readPDFFile(file) {
        const reader = new FileReader();
        reader.onloadend = () => {
            this.sendFile(reader.result, true);
        };
        reader.onerror = err => {
            this.setError('There was an error reading the PDF file: ' + err.toString());
        };
        reader.readAsDataURL(file);
    }

    processFile(dataURL, fileType) {
        const image = new Image();
        image.src = dataURL;

        image.onload = () => {
            let {width, height} = image;

            // ensure image is not bigger than 1600 x 1600
            const maxWidth = 1600;
            const maxHeight = 1600;
            const shouldResize = (width > maxWidth) || (height > maxHeight);
            if (shouldResize) {
                if (width > height) {
                    height = height * (maxWidth / width);
                    width = maxWidth;
                } else {
                    width = width * (maxHeight / height);
                    height = maxHeight;
                }
            }

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = width;
            canvas.height = height;

            // resize
            context.translate(canvas.width/2, canvas.height/2);
            context.translate(-canvas.width/2, -canvas.height/2);
            context.drawImage(image, -(width-canvas.width)/2, -(height-canvas.height)/2, width, height);

            dataURL = canvas.toDataURL(fileType);
            this.sendFile(dataURL, false);
        };

        image.onerror = err => {
            this.setError('There was an error processing your file: ' + err.toString());
        };
    }

    dataURItoBlob(dataURI) {
        let byteString;
        if (dataURI.split(',')[0].indexOf('base64') >= 0) {
            byteString = atob(dataURI.split(',')[1]);
        } else {
            byteString = unescape(dataURI.split(',')[1]);
        }
        let [mimeString] = dataURI.split(',')[0].split(':')[1].split(';');
        let ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ia], {type:mimeString});
    }

    sendFile(dataURL, isPDF) {
        const {stip: {docType}, indexOfDoc, applicantType, offerId} = this.props;

        this.setState({
            uploading: true,
            uploadingPercentage: 0,
            isPDF
        });

        if (!isPDF) {
            StipsService.setPreviewImage(docType, dataURL, indexOfDoc);
        }

        var blob = this.dataURItoBlob(dataURL);
        var formData = new FormData();
        formData.append('file', blob);
        uploadImage({
            offerId,
            stipType: docType,
            applicantType,
            indexOfDoc
        }, formData, {
            onUploadProgress: e => {
                if (e.lengthComputable) {
                    const uploadingPercentage = Math.round((e.loaded / e.total) * 100);
                    this.setState({
                        uploadingPercentage
                    });
                }
            }
        }).then(() => {
            MixpanelService.stipImageUpload(true);
            this.setState({
                uploading: false,
                uploadingPercentage: 0,
                error: null,
                didUpload: true
            });

            if (isPDF) {
                this.downloadPDFImage();
            }
        }).catch(err => {
            this.setState({
                uploading: false,
                uploadingPercentage: 0,
                dataURL: undefined
            });
            this.setError(err);
        });
    }

    downloadPDFImage() {
        const {stip: {docType}, indexOfDoc, applicantType, offerId} = this.props;
        this.setState({
            downloading: true
        });
        downloadImage({
            offerId,
            stipType: docType,
            applicantType,
            indexOfDoc
        }).then(res => {
            const {dataURL} = res.data;
            if (dataURL) {
                StipsService.setPreviewImage(docType, dataURL, indexOfDoc);
                this.setState({
                    downloading: false
                });
            }
        }).catch(err => {
            this.setState({
                downloading: false
            });
            this.setError(err.toString());
        });
    }

    getPhotoTitle() {
        if (isMobile()) {
            const {imageData} = this.props;
            if (imageData) {
                return 'Retake';
            }
            return 'Photo';
        }
        return 'Upload';
    }

    renderUploadButton() {
        const {classes = {}} = this.props;

        return (
            <div>
                <input
                    id="file"
                    type="file"
                    className={classes.input}
                    accept="image/*;capture=camera"
                    onChange={this.handleRetakeSingle}
                />
                <label htmlFor="file">{this.getPhotoTitle()}</label>
            </div>
        );
    }

    renderSaveButton() {
        const {downloading, didUpload} = this.state;

        return <Button tag="a" onClick={this.handleClose}>{didUpload && !downloading ? 'Save' : 'Close'}</Button>;
    }

    render() {
        const {stip, imageData, doc, classes = {}, applicantName, vinVehicleInfo, coAppUnlocked} = this.props;
        const {docType} = stip;
        const {downloading, uploading, error, isPDF} = this.state;
        const uploadButton = this.renderUploadButton();
        const saveButton = this.renderSaveButton();

        let subtitle;
        if (['DL1', 'DL2', 'POI'].includes(docType) && coAppUnlocked) {
            subtitle = `For ${applicantName}`;
        } else if (docType.match('INS')) {
            subtitle = vinVehicleInfo;
        }

        return (
            <Dialog alertCls={classes.root}>
                <p className="info" style={subtitle ? {marginBottom: 0} : undefined}>
                    <strong>{doc.title}</strong>
                </p>
                {subtitle && <p className="info">{subtitle}</p>}
                <div className={classes.imgContainer}>
                    {(downloading || uploading) && <Loader />}
                    {(!isPDF || !(downloading || uploading)) && (
                        <img src={imageData || getStipIcon(docType)} />
                    )}
                    <Dropzone
                        className={classes.dropZone}
                        accept="image/*,application/pdf,capture=camera"
                        onDrop={(acceptedFiles, rejectedFiles) => this.handleRetake(acceptedFiles, rejectedFiles)}
                    />
                </div>
                <div className={classes.footer}>
                    {error && (
                        <p className="info">
                            {renderError(error)}
                        </p>
                    )}
                    {!error && !imageData && (
                        <p className={`info ${classes.please}`}>
                            Make sure the photo isn’t blurry or cut off before you submit.
                        </p>
                    )}

                    <StepButtons buttons={[uploadButton, saveButton]} />
                </div>
            </Dialog>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const {vehicleInfo} = state;
    const {stip, doc, errorHandler} = ownProps.data;
    const indexOfDoc = stip.docs.findIndex(stipDoc => stipDoc._id === doc._id);
    const applicantName = applicantNameSelector(state);
    const vinVehicleInfo = getVinVehicleInfo(vehicleInfo);

    return {
        imageData: findImageData(state, stip.docType, indexOfDoc),
        applicantType: state.stips.applicantType,
        coAppUnlocked: state.navigation.coAppUnlocked,
        offerId: getOfferId(),
        applicantName,
        vinVehicleInfo,
        indexOfDoc,
        stip,
        doc,
        errorHandler
    };
};

export default connect(mapStateToProps)(injectSheet(styles)(StipDialog));
